import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable, Injector, ProviderToken } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, throwError } from 'rxjs';
import { retry, catchError, switchMap } from 'rxjs/operators';

import { ErrorDetails } from '@shared/models';
import { AuthService, TokenService } from '@shared/services';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<ErrorDetails>> {
    return next.handle(request).pipe(
      retry(0),
      catchError((error: HttpErrorResponse) => {
        const translate = this.tryGet(TranslateService);
        const tokenService = this.tryGet(TokenService);

        console.error(error);

        let errorDetails: ErrorDetails;

        if (error.status === 0) {
          errorDetails = this.buildErrorDetails(
            0,
            this.translate(translate, 'error.noInternetConnection', 'No internet connection')
          );
        } else if (error.status === HttpStatusCode.PayloadTooLarge) {
          errorDetails = this.buildErrorDetails(
            error.status,
            this.translate(translate, 'error.payloadTooLarge', 'The data is too large for the server to accept.')
          );
        } else if (!error.error) {
          errorDetails = this.buildErrorDetails(
            error.status,
            this.translate(translate, 'error.serverError', 'Server error')
          );
        } else {
          errorDetails = error.error;
        }

        if (error.status === 401 && tokenService?.isUserLoggedIn()) {
          return this.handleTokenExpired(request, next);
        }

        return throwError(() => errorDetails);
      })
    );
  }

  private handleTokenExpired(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<ErrorDetails>> {
    const authService = this.tryGet(AuthService);
    if (!authService) {
      return throwError(() => request);
    }

    if (request.url.includes('auth/refresh-token')) {
      authService.logout();
      return of(null);
    }
    return authService.refreshToken('').pipe(
      switchMap(() => {
        return next.handle(this.addToken(request, authService.getAuthorizationToken()));
      }),
      catchError((error) => {
        authService.logout();
        return throwError(() => error);
      })
    );
  }

  private addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private tryGet<T>(token: ProviderToken<T>): T | null {
    try {
      return this.injector.get(token);
    } catch {
      return null;
    }
  }

  private translate(translateService: TranslateService | null, key: string, fallback: string): string {
    const value = translateService?.instant(key);
    return value && value !== key ? value : fallback;
  }

  private buildErrorDetails(statusCode: number, message: string): ErrorDetails {
    return {
      statusCode,
      message,
      details: null!,
      logId: null!,
      errors: null!,
    };
  }
}
